import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import PhotoUploader from './PhotoUploader';
import { useInstaller } from '../../contexts/InstallerContext';
import { useJobs } from '../../hooks/useJobs';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase-init';
import { sendJobCompletedEmail } from '../../services/installer-notification-service';
import toast from 'react-hot-toast';

export default function CompleteJobForm({ isOpen, onClose, job }) {
  const navigate = useNavigate();
  const installer = useInstaller();
  const { refreshJobs } = useJobs();
  
  const [showPhotoUploader, setShowPhotoUploader] = useState(false);
  const [photoType, setPhotoType] = useState('before');
  const [customerFeedback, setCustomerFeedback] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const beforePhotos = job.installerPhotos?.filter(p => p.type === 'before') || [];
  const afterPhotos = job.installerPhotos?.filter(p => p.type === 'after') || [];

  const handlePhotoUploadComplete = () => {
    refreshJobs();
    toast.success('Photos uploaded successfully!');
  };

  const handleSubmit = async () => {
    // ✅ REMOVED PHOTO VALIDATION - Photos are now optional!
    setSubmitting(true);

    const completionToast = toast.loading('Completing job...');

    try {
      const jobRef = doc(db, 'jobs', job.id);
      
      await updateDoc(jobRef, {
        status: 'completed',
        completedBy: installer.name,
        completedByUid: installer.uid,
        customerFeedback: customerFeedback.trim() || null,
        completionNotes: additionalNotes.trim() || null,
        completedAt: Timestamp.now(),
        statusHistory: arrayUnion({
          status: 'completed',
          timestamp: Timestamp.now(),
          note: `Completed by ${installer.name}`,
          installerName: installer.name
        })
      });

      // Send email notification
      await sendJobCompletedEmail(job, installer, customerFeedback, additionalNotes);

      console.log('✅ Job completed successfully');

      toast.success('✅ Job completed! Customer has been notified.', {
        id: completionToast,
        duration: 5000,
        icon: '🎉'
      });
      
      // Close modal and navigate
      onClose();
      
      setTimeout(() => {
        navigate('/installer');
      }, 1500);

    } catch (error) {
      console.error('❌ Error completing job:', error);
      
      toast.error(`Failed to complete job: ${error.message}`, {
        id: completionToast,
        duration: 6000
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="✅ Complete Job"
        size="large"
      >
        <div style={{
          maxHeight: '70vh',
          overflowY: 'auto',
          padding: '24px'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#2b2b2b',
            marginBottom: '8px'
          }}>
            #{job.contractNumber} - {job.customerName || job.name}
          </div>

          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '24px'
          }}>
            Upload photos and provide feedback (all optional).
          </div>

          {/* ✅ BEFORE PHOTOS - NOW OPTIONAL */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '12px'
            }}>
              Before Photos (optional)
            </div>

            {beforePhotos.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '12px'
              }}>
                {beforePhotos.map((photo, index) => (
                  <div key={index} style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    background: '#f5f2ed',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={photo.url}
                      alt={`Before ${index + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '4px',
                      left: '4px',
                      right: '4px',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      fontSize: '10px',
                      padding: '4px',
                      borderRadius: '4px',
                      textAlign: 'center',
                      fontWeight: '600'
                    }}>
                      Before {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => {
                setPhotoType('before');
                setShowPhotoUploader(true);
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '2px dashed #3b82f6',
                borderRadius: '12px',
                color: '#3b82f6',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                minHeight: '44px'
              }}
            >
              + Upload Before Photos ({beforePhotos.length})
            </button>
          </div>

          {/* ✅ AFTER PHOTOS - NOW OPTIONAL */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '12px'
            }}>
              After Photos (optional)
            </div>

            {afterPhotos.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '12px'
              }}>
                {afterPhotos.map((photo, index) => (
                  <div key={index} style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    background: '#f5f2ed',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={photo.url}
                      alt={`After ${index + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '4px',
                      left: '4px',
                      right: '4px',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      fontSize: '10px',
                      padding: '4px',
                      borderRadius: '4px',
                      textAlign: 'center',
                      fontWeight: '600'
                    }}>
                      After {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => {
                setPhotoType('after');
                setShowPhotoUploader(true);
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '2px dashed #22c55e',
                borderRadius: '12px',
                color: '#22c55e',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                minHeight: '44px'
              }}
            >
              + Upload After Photos ({afterPhotos.length})
            </button>
          </div>

          {/* CUSTOMER FEEDBACK */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '8px'
            }}>
              Customer Feedback (optional)
            </label>
            <textarea
              value={customerFeedback}
              onChange={(e) => setCustomerFeedback(e.target.value)}
              placeholder="How was the customer's experience?"
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                border: '2px solid #e5e5e5',
                borderRadius: '12px',
                fontSize: '16px',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8B4513'}
              onBlur={(e) => e.target.style.borderColor = '#e5e5e5'}
            />
          </div>

          {/* ADDITIONAL NOTES */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '8px'
            }}>
              Additional Notes (optional)
            </label>
            <textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Any issues or comments about this job?"
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                border: '2px solid #e5e5e5',
                borderRadius: '12px',
                fontSize: '16px',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8B4513'}
              onBlur={(e) => e.target.style.borderColor = '#e5e5e5'}
            />
          </div>
        </div>

        {/* FOOTER BUTTONS */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e5e5',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          background: '#fafafa'
        }}>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
            style={{ minHeight: '44px' }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: '#22c55e',
              minHeight: '44px'
            }}
          >
            {submitting ? 'Completing...' : 'Complete Job'}
          </Button>
        </div>
      </Modal>

      {showPhotoUploader && (
        <PhotoUploader
          isOpen={showPhotoUploader}
          onClose={() => setShowPhotoUploader(false)}
          jobId={job.id}
          photoType={photoType}
          onUploadComplete={handlePhotoUploadComplete}
        />
      )}
    </>
  );
}